import { addSeconds, max } from 'date-fns';
import { ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import type { ChatState, QueriesState } from './interfaces';

export function queriesReducer(state: QueriesState.State, action: ChatState.Action): QueriesState.State {

  switch (action.type) {

    case 'query-submitted': {
      state[action.payload.identifier] = {
        attachments: action.payload.attachments || [],
        //Make sure that the new message always shows up as the latest message, this handles cases where the browser clock is behind the server clock
        //We're going to overwrite this value when the server comes back with the message anyway but this makes for a better UX in the meantime
        createdOn: getSafeCreatedOn(),
        id: null,
        identifier: action.payload.identifier,
        value: action.payload.value,
        rating: null,
        response: null,
        state: null,
        status: ChatQueryStatus.Loading,
        citations: [],
        mapping: {
          parentIdentifier: action.payload.parentQueryIdentifier,
          childrenIdentifiers: [],
        },
        suggestedFollowups: [],
        hideUserMessage: action.payload.hideUserMessage,
        metadata: {},
        agentProgress: null,
      };

      // remove siblings that are errors, basically only persist 1 errored message for display purposes.
      // const siblings = getQuerySiblings({
      //   queryIdentifier: action.payload.identifier,
      //   queries: state,
      // });

      // const erroredIdentifiers = siblings.filter(q => q.status === 'error').map(q => q.identifier);

      // for (const errorIdentifier of erroredIdentifiers) {
      //   delete state[errorIdentifier];
      // }
      const erroredIdentifiers = [];

      // update parent query, add this submitted variant as a child to its children mapping
      if (action.payload.parentQueryIdentifier) {
        const parent = state[action.payload.parentQueryIdentifier];
        state[action.payload.parentQueryIdentifier] = {
          ...parent,
          mapping: {
            ...parent.mapping,
            childrenIdentifiers: [
              ...parent.mapping.childrenIdentifiers,
              action.payload.identifier,
            ].filter(identifier => !erroredIdentifiers.includes(identifier)),
          },
        };
      }

      return Object.assign({}, state);
    }

    case 'query-variant-submitted': {
      const source = state[action.payload.sourceIdentifier];
      state[action.payload.identifier] = {
        attachments: action.payload.attachments || [],
        createdOn: getSafeCreatedOn(),
        id: null,
        identifier: action.payload.identifier,
        value: source.value,
        rating: null,
        response: null,
        state: {},
        status: ChatQueryStatus.Loading,
        citations: [],
        mapping: {
          parentIdentifier: source.mapping.parentIdentifier,
          childrenIdentifiers: [],
        },
        suggestedFollowups: [],
        hideUserMessage: action.payload.hideUserMessage,
        metadata: {},
        agentProgress: null,
      };

      // remove siblings that are errors, basically only persist 1 errored message for display purposes.
      // const siblings = getQuerySiblings({
      //   queryIdentifier: source.identifier,
      //   queries: state,
      // });

      // const erroredIdentifiers = siblings.filter(q => q.status === 'error').map(q => q.identifier);

      // for (const errorIdentifier of erroredIdentifiers) {
      //   delete state[errorIdentifier];
      // }
      const erroredIdentifiers = [];

      // update parent query, add this submitted variant as a child to its children mapping
      if (source.mapping.parentIdentifier) {
        state[source.mapping.parentIdentifier] = {
          ...state[source.mapping.parentIdentifier],
          mapping: {
            ...state[source.mapping.parentIdentifier].mapping,
            childrenIdentifiers: [
              ...state[source.mapping.parentIdentifier].mapping.childrenIdentifiers,
              action.payload.identifier,
            ].filter(identifier => !erroredIdentifiers.includes(identifier)),
          },
        };
      }

      return Object.assign({}, state);
    }

    case 'query-response-available':
    case 'query-variant-response-available': {
      state[action.payload.query.identifier] = {
        ...state[action.payload.query.identifier],
        createdOn: action.payload.query.createdOn,
        attachments: action.payload.query.attachments || [],
        citations: action.payload.query.citations,
        id: action.payload.query.id,
        response: action.payload.query.response,
        mapping: action.payload.query.mapping,
        status: ChatQueryStatus.Complete,
        suggestedFollowups: action.payload.query.suggestedFollowups,
        langfuseTraceUrl: action.payload.query.langfuseTraceUrl,
        state: action.payload.query.state,
        metadata: action.payload.query.metadata,
        agentProgress: action.payload.query.agentProgress ?? null,
      };

      return Object.assign({}, state);
    }

    case 'query-response-streaming': {
      if (!state[action.payload.queryIdentifier]) {
        console.warn(`Query with identifier ${action.payload.queryIdentifier} not found for streaming update.`);
        return state;
      }
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.Streaming,
        response: {
          ...state[action.payload.queryIdentifier].response,
          type: ChatResponseType.MarkdownHtml,
          value: (state[action.payload.queryIdentifier].response?.value || '') + action.payload.textChunk,
        },
      };
      return Object.assign({}, state);
    }

    case 'query-errored':
    case 'query-variant-errored': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.Error,
        sentryTraceId: action.payload.traceId,
        langfuseTraceUrl: action.payload.langfuseTraceUrl,
      };
      return Object.assign({}, state);
    }

    case 'query-rated': {
      state[action.payload.queryIdentifier] = Object.assign({}, state[action.payload.queryIdentifier], {
        rating: action.payload.rating,
      });
      return Object.assign({}, state);
    }

    case 'query-taking-long': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.LoadingExtended,
      };
      return Object.assign({}, state);
    }

    case 'query-followups-available': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        suggestedFollowups: action.payload.items,
        status: ChatQueryStatus.FollowupsAvailable,
      };
      return Object.assign({}, state);
    }

    case 'query-updated': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        ...action.payload.query,
      };
      return Object.assign({}, state);
    }

    case 'query-cancelled': {
      state[action.payload.queryIdentifier] = {
        ...state[action.payload.queryIdentifier],
        status: ChatQueryStatus.Cancelled,
      };
      return Object.assign({}, state);
    }

    default:
      return state;
  }

  function getSafeCreatedOn() {
    return max([new Date(), ...Object.values(state).map(q => addSeconds(q.createdOn, 1))]);
  }
}